import { Controller } from "@hotwired/stimulus"
import { createI18nGetter } from "../lib/i18n_helper"
import { showSuccess, showError, showWarning } from "../lib/platform_toast"
import {
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribed as checkIsSubscribed,
  isSupported as checkIsSupported
} from "../lib/push_subscription_service"

/**
 * Push Subscription Controller
 *
 * Manages Web Push notification subscription lifecycle:
 * - Permission request and status tracking
 * - Service Worker registration
 * - Push subscription via VAPID
 * - Backend synchronization
 *
 * @connects data-controller="push-subscription"
 */
export default class extends Controller {
  static targets = ["toggle", "status"]

  connect() {
    this.i18n = createI18nGetter(this)
    this.checkPermissionAndUpdateUI()
  }

  // ─────────────────────────────────────────────────────────────
  // UI State Management
  // ─────────────────────────────────────────────────────────────

  async checkPermissionAndUpdateUI() {
    if (this.isTurboNative() || !checkIsSupported()) {
      this.updateStatus("unsupported")
      this.setToggleState(false, true)
      return
    }

    const permission = Notification.permission

    if (permission === "granted") {
      if (this.isSubscribing) return

      const subscribed = await checkIsSubscribed()

      if (this.isSubscribing) return

      this.updateStatus(subscribed ? "subscribed" : "granted")
      this.setToggleState(subscribed)
    } else if (permission === "denied") {
      this.updateStatus("denied")
      this.setToggleState(false, true)
    } else {
      this.updateStatus("default")
    }
  }

  /**
   * Toggle handler - delegates to subscribe/unsubscribe
   */
  async toggle(event) {
    if (event.target.checked) {
      await this.subscribe()
    } else {
      await this.unsubscribe()
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Subscription Lifecycle
  // ─────────────────────────────────────────────────────────────

  async subscribe() {
    if (Notification.permission === "denied") {
      showError(this.i18n("errorsPermissionDenied"))
      this.updateStatus("denied")
      this.setToggleState(false, true)
      return
    }

    if (this.isTurboNative() || !checkIsSupported()) {
      showError(this.i18n("errorsUnsupported"))
      this.updateStatus("unsupported")
      this.setToggleState(false, true)
      return
    }

    if (this.isSubscribing) return
    this.isSubscribing = true

    try {
      const permission = await Notification.requestPermission()

      if (permission !== "granted") {
        showWarning(this.i18n("errorsPermissionRequired"))
        this.updateStatus(permission === "denied" ? "denied" : "default")
        this.setToggleState(false, permission === "denied")
        return
      }

      const success = await subscribeToPush()
      if (!success) {
        throw new Error(this.i18n("errorsBackendFailed"))
      }

      this.updateStatus("subscribed")
      this.setToggleState(true)
      showSuccess(this.i18n("successSubscribed"))

    } catch (error) {
      console.error("[PushSubscription] Subscribe error:", error)
      showError(this.i18n("errorsSubscribeFailed", "", { error: error.message }))
      this.setToggleState(false)
      this.updateStatus("default")
    } finally {
      this.isSubscribing = false
    }
  }

  async unsubscribe() {
    try {
      const success = await unsubscribeFromPush()
      if (success) {
        this.updateStatus("granted")
        showSuccess(this.i18n("successUnsubscribed"))
      }
    } catch (error) {
      console.error("[PushSubscription] Unsubscribe error:", error)
      showError(this.i18n("errorsUnsubscribeFailed", "", { error: error.message }))
      this.setToggleState(true)
    }
  }

  // ─────────────────────────────────────────────────────────────
  // UI Helpers
  // ─────────────────────────────────────────────────────────────

  setToggleState(checked, disabled = false) {
    if (!this.hasToggleTarget) return

    this.toggleTarget.checked = checked
    if (disabled) {
      this.toggleTarget.disabled = true
    }
  }

  updateStatus(status) {
    if (!this.hasStatusTarget) return

    const statusKey = `status${status.charAt(0).toUpperCase()}${status.slice(1)}`
    this.statusTarget.textContent = this.i18n(statusKey) || ""
  }

  isTurboNative() {
    return document.documentElement.classList.contains("turbo-native") ||
      navigator.userAgent?.includes("Turbo Native")
  }
}
